import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@prio365/prio365-react-library';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import Flex from '../../../../components/Flex';
import { PrioShareDto } from '../../../../models/PrioShare';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import { formatDate } from '../../../../util/helpers';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    width: '100%',
    padding: theme.spacing.regular,
    borderRadius: theme.borderRadius.regular,
    '&:hover': {
      backgroundColor: theme.colors.application.background.light,
    },
    '&:hover $actions': {
      opacity: 1,
    },
  },
  title: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: 2,
  },
  metadataRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.regular,
    marginTop: theme.spacing.small,
  },
  metadataItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.extraSmall,
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
  },
  actions: {
    opacity: 0,
    transition: 'opacity 0.15s ease',
  },
  deleteButton: {
    color: `${theme.colors.base.red.default} !important`,
  },
}));

interface PrioShareItemCardProps {
  className?: string;
  item: PrioShareDto;
  projectName?: string;
  onView: (item: PrioShareDto) => void;
  onCopyLink: (prioShareId: string) => void;
  onDeactivate: (item: PrioShareDto) => void;
  onEdit?: (item: PrioShareDto) => void;
  onDelete?: (item: PrioShareDto) => void;
}

const PrioShareItemCard: React.FC<PrioShareItemCardProps> = ({
  className,
  item,
  projectName,
  onView,
  onCopyLink,
  onDeactivate,
  onEdit,
  onDelete,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const recipients = useMemo(() => item.recipients ?? [], [item.recipients]);
  const files = useMemo(() => item.files ?? [], [item.files]);

  const downloadCount = useMemo(
    () =>
      recipients.filter((r) =>
        r.downloads.some((d) => d.downloadSuccessful)
      ).length,
    [recipients]
  );

  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <div className={classNames(classes.root, className)}>
      <Flex.Row alignItems="flex-start" justifyContent="space-between">
        <Flex.Column flex={1} minWidth={0}>
          <span className={classes.title}>{item.name}</span>
          {projectName && (
            <span className={classes.subtitle}>{projectName}</span>
          )}
          <div className={classes.metadataRow}>
            <span className={classes.metadataItem}>
              <FontAwesomeIcon icon={['fal', 'calendar']} />
              {formatDate(item.expirationDate)}
            </span>
            <span className={classes.metadataItem}>
              <FontAwesomeIcon icon={['fal', 'files']} />
              {files.length}
            </span>
            {item.state !== 'draft' && (
              <span className={classes.metadataItem}>
                <FontAwesomeIcon icon={['fal', 'download']} />
                {downloadCount}/{recipients.length}
              </span>
            )}
            <span className={classes.metadataItem}>
              <FontAwesomeIcon icon={['fal', 'users']} />
              {recipients.length}
            </span>
          </div>
        </Flex.Column>

        <Flex.Row childrenGap={theme.spacing.extraSmall} className={classes.actions}>
          {item.state === 'draft' ? (
            <>
              <Button
                type="link"
                iconProp={['fal', 'pen']}
                size="small"
                tooltip={t('common:widgetArea.prioShareWidget.tooltips.edit')}
                onClick={() => onEdit?.(item)}
              />
              <Button
                type="danger-link"
                iconProp={['fal', 'trash']}
                size="small"
                className={classes.deleteButton}
                tooltip={t('common:widgetArea.prioShareWidget.tooltips.delete')}
                onClick={() => onDelete?.(item)}
              />
            </>
          ) : (
            <>
              <Button
                type="link"
                iconProp={['fal', 'eye']}
                size="small"
                tooltip={t('common:widgetArea.prioShareWidget.tooltips.view')}
                onClick={() => onView(item)}
              />
              <Button
                type="link"
                iconProp={['fal', 'copy']}
                size="small"
                tooltip={t('common:widgetArea.prioShareWidget.tooltips.copyLink')}
                onClick={() => onCopyLink(item.prioShareId)}
              />
              {item.state === 'active' && (
                <Button
                  type="danger-link"
                  iconProp={['fal', 'archive']}
                  size="small"
                  tooltip={t('common:widgetArea.prioShareWidget.tooltips.archive')}
                  onClick={() => onDeactivate(item)}
                />
              )}
            </>
          )}
        </Flex.Row>
      </Flex.Row>
    </div>
  );
};

export default PrioShareItemCard;
